import { TabsList } from "components/ui/tabs";
import { useSideBarTabsWidthReporter } from "pages/project/common/SideBar";
import { measureTabsStripWidthPx, sideBarTabsListClassName } from "pages/project/common/masterDetailLayout";
import { ComponentPropsWithoutRef, ElementRef, forwardRef, useCallback, useLayoutEffect, useRef } from "react";
import { cn } from "utils/ui";

type Props = ComponentPropsWithoutRef<typeof TabsList>;

/**
 * TabsList that reports its intrinsic strip width to SideBar so the shell can grow
 * for 4+ tabs without depending on active tab content.
 */
export const SideBarTabsList = forwardRef<ElementRef<typeof TabsList>, Props>(function SideBarTabsList(
  { className, children, ...props },
  ref
) {
  const reportTabsWidthPx = useSideBarTabsWidthReporter();
  const localRef = useRef<HTMLDivElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  useLayoutEffect(() => {
    const el = localRef.current;
    if (!el) {
      return;
    }

    const measure = () => {
      reportTabsWidthPx(measureTabsStripWidthPx(el));
    };

    const observeTabs = (ro: ResizeObserver) => {
      el.querySelectorAll('[role="tab"]').forEach((tab) => ro.observe(tab));
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    observeTabs(ro);

    const mo = new MutationObserver(() => {
      observeTabs(ro);
      measure();
    });
    mo.observe(el, { childList: true, subtree: true, characterData: true });

    return () => {
      ro.disconnect();
      mo.disconnect();
      reportTabsWidthPx(null);
    };
  }, [reportTabsWidthPx]);

  return (
    <TabsList ref={setRefs} className={cn(sideBarTabsListClassName, className)} {...props}>
      {children}
    </TabsList>
  );
});
